import {
  Box,
  Typography,
  Chip,
  Button,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IChangeReview } from "../../../../types/review";
import { useCarWash } from "../../../../context/CarWashContext";
import {
  processReview,
  getCarWashReviews,
} from "../../../../services/carWash.service";
import ReviewDetailsModal from "./components/ReviewDetailsModal";

type Props = {};

const Reviews = (props: Props) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<IChangeReview | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [reviewToProcess, setReviewToProcess] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [allReviews, setAllReviews] = useState<IChangeReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const { carWash, loading: carWashLoading } = useCarWash();
  const { carWashID } = useParams<{ carWashID: string }>();

  // Fetch all reviews for this car wash — use URL param directly to avoid
  // waiting on the CarWashContext async load before triggering the fetch.
  useEffect(() => {
    if (!carWashID) return;
    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        const response = await getCarWashReviews(carWashID);
        setAllReviews(response.data);
      } catch (error: any) {
        setSnackbar({
          open: true,
          message: error.message || t("reviews.fetchFailed"),
          severity: "error",
        });
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();
    // Re-runs only when carWashID changes; `t` changes identity on
    // language switch, and re-running this fetch because the UI language
    // changed is not wanted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carWashID]);

  // Split reviews by status
  const { pendingReviews, processedReviews } = useMemo(() => {
    const pending = allReviews.filter((review) => review.status === "pending");
    const processed = allReviews.filter(
      (review) => review.status !== "pending"
    );
    return { pendingReviews: pending, processedReviews: processed };
  }, [allReviews]);

  const handleProcessReview = async (
    reviewId: string,
    status: "approved" | "rejected",
    comment?: string
  ) => {
    if (!carWash?._id) return;
    setActionLoading(reviewId);
    try {
      await processReview(carWash._id, reviewId, status, comment);

      // Refetch all reviews after processing
      const response = await getCarWashReviews(carWash._id);
      setAllReviews(response.data);

      setSnackbar({
        open: true,
        message:
          status === "approved"
            ? t("reviews.approvedSuccess")
            : t("reviews.rejectedSuccess"),
        severity: "success",
      });
      if (status === "rejected") {
        setRejectDialogOpen(false);
        setRejectComment("");
        setReviewToProcess(null);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || t("reviews.processFailed"),
        severity: "error",
      });

      // Reconcile with the server before giving up.
      //
      // Without this the table kept showing whatever it held before the
      // request, so a failed approve left the row sitting in "Pending Reviews"
      // and only a manual page reload revealed the truth. That is precisely how
      // the 2026-09-23 bug presented: an error toast, an apparently untouched
      // row, and a review that turned out to be approved after a refresh.
      //
      // Guarded separately so a failed refetch cannot replace the real error
      // message with a less useful one.
      try {
        const response = await getCarWashReviews(carWash._id);
        setAllReviews(response.data);
      } catch {
        /* keep the original error on screen */
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveClick = (reviewId: string) => {
    handleProcessReview(reviewId, "approved");
  };

  const handleRejectClick = (reviewId: string) => {
    setReviewToProcess(reviewId);
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = () => {
    if (reviewToProcess) {
      handleProcessReview(reviewToProcess, "rejected", rejectComment);
    }
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectComment("");
    setReviewToProcess(null);
  };

  const handleViewDetails = (review: IChangeReview) => {
    setSelectedReview(review);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReview(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getRequestTypeLabel = (type: string) => {
    switch (type) {
      case "carWash":
        return t("reviews.typeNewCarWash");
      case "contactPerson":
        return t("reviews.typeContactPersonChange");
      case "price":
        return t("reviews.typePriceChange");
      default:
        return type;
    }
  };

  const getReviewStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return t("reviews.statusPending");
      case "approved":
        return t("reviews.statusApproved");
      case "rejected":
        return t("reviews.statusRejected");
      default:
        return status;
    }
  };

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case "carWash":
        return <BusinessIcon />;
      case "contactPerson":
        return <PersonIcon />;
      case "price":
        return <MoneyIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return t("common.notAvailable");
    return new Date(date).toLocaleString();
  };

  const getCarWashName = (carWash: IChangeReview["carWash"]) => {
    if (!carWash) return t("common.notAvailable");
    if (typeof carWash === "string") return carWash;
    return carWash.legalName || carWash._id || t("common.unknown");
  };

  const renderReviewsTable = (
    reviewsList: IChangeReview[],
    showActions: boolean
  ) => {
    if (reviewsList.length === 0) return null;

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("reviews.colType")}</TableCell>
              <TableCell>{t("reviews.colOwner")}</TableCell>
              <TableCell>{t("reviews.colCarWash")}</TableCell>
              <TableCell>{t("reviews.colRequested")}</TableCell>
              <TableCell>{t("common.status")}</TableCell>
              <TableCell align="right">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviewsList.map((review) => {
              const ownerName =
                typeof review.owner === "string"
                  ? t("common.unknown")
                  : `${review.owner.firstName || ""} ${
                      review.owner.lastName || ""
                    }`.trim() || t("common.unknown");

              return (
                <TableRow key={review._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getRequestTypeIcon(review.requestType)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {getRequestTypeLabel(review.requestType)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                      <Typography variant="body2">{ownerName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getCarWashName(review.carWash)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(review.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getReviewStatusLabel(review.status)}
                      color={
                        review.status === "pending"
                          ? "warning"
                          : review.status === "approved"
                          ? "success"
                          : "error"
                      }
                      size="small"
                      icon={
                        review.status === "pending" ? (
                          <PendingIcon />
                        ) : review.status === "approved" ? (
                          <CheckCircleIcon />
                        ) : (
                          <CancelIcon />
                        )
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDetails(review)}
                      >
                        {t("common.view")}
                      </Button>
                      {showActions && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleApproveClick(review._id!)}
                            disabled={actionLoading === review._id}
                          >
                            {actionLoading === review._id ? (
                              <CircularProgress size={20} />
                            ) : (
                              t("common.approve")
                            )}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => handleRejectClick(review._id!)}
                            disabled={actionLoading === review._id}
                          >
                            {t("common.reject")}
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (carWashLoading || reviewsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3} borderRadius={2} boxShadow={3} bgcolor="background.paper">
      {/* Pending Reviews Section */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" mb={3}>
          <PendingIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5">{t("carWashes.tabPendingReviews")}</Typography>
          <Chip
            label={pendingReviews.length}
            color="warning"
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>

        {pendingReviews.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Box textAlign="center" py={4}>
                <PendingIcon
                  sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  {t("reviews.noPending")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("reviews.allProcessed")}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          renderReviewsTable(pendingReviews, true)
        )}
      </Box>

      {/* Review History Section */}
      {processedReviews.length > 0 && (
        <Box>
          <Box display="flex" alignItems="center" mb={3}>
            <CheckCircleIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="h5">{t("reviews.history")}</Typography>
            <Chip
              label={processedReviews.length}
              color="default"
              size="small"
              sx={{ ml: 2 }}
            />
          </Box>
          {renderReviewsTable(processedReviews, false)}
        </Box>
      )}

      {/* Review Details modal (shared, responsive) */}
      <ReviewDetailsModal
        open={dialogOpen}
        review={selectedReview}
        onClose={handleCloseDialog}
        onApprove={(id) => handleProcessReview(id, "approved")}
        onReject={(id, comment) =>
          handleProcessReview(id, "rejected", comment)
        }
        actionLoading={actionLoading === selectedReview?._id}
      />

      {/* Reject Review Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleCloseRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("reviews.rejectReview")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("reviews.rejectReasonHelp")}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t("reviews.rejectionReason")}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            placeholder={t("reviews.rejectionReasonPlaceholder")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleConfirmReject}
            disabled={
              actionLoading === reviewToProcess || !rejectComment.trim()
            }
          >
            {t("reviews.confirmRejection")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reviews;
